/**
 * ToolsField – Rich text editor with toolbar (Bold, Italic, Underline, Align) and optional Voice-to-Text (mic).
 * Migrated from admin4.0 ToolsField/ToolsFieldWithMic; voiceToText flow matches existing.
 */
import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Grid,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import TableChartIcon from "@mui/icons-material/TableChart";
import MicIcon from "@mui/icons-material/Mic";
import VoiceToText from "./VoiceToText";

function removeEmptyStyleTagsAndReplaceSpan(content) {
  if (!content || content === "<br>") return "";
  const div = document.createElement("div");
  div.innerHTML = content;

  const elements = div.querySelectorAll("*[style]");
  elements.forEach((element) => {
    const style = element.getAttribute("style");
    const retainedStyles = (style || "")
      .split(";")
      .map((r) => r.trim())
      .filter((r) => {
        const [property, value] = r.split(":").map((s) => s?.trim());
        return property === "text-align" && ["center", "left", "right"].includes(value);
      })
      .join("; ");
    if (retainedStyles) element.setAttribute("style", retainedStyles);
    else element.removeAttribute("style");
  });

  div.querySelectorAll("span").forEach((span) => {
    const newDiv = document.createElement("div");
    Array.from(span.attributes).forEach((attr) => newDiv.setAttribute(attr.name, attr.value));
    while (span.firstChild) newDiv.appendChild(span.firstChild);
    span.parentNode.replaceChild(newDiv, span);
  });

  const firstDiv = div.firstElementChild;
  if (firstDiv && firstDiv.tagName.toLowerCase() === "div") {
    const style = firstDiv.getAttribute("style");
    if (style && style.includes("text-align")) {
      const alignValue = style.match(/text-align:\s*(left|right|center)/);
      firstDiv.setAttribute("style", alignValue ? alignValue[0] : "text-align: left;");
    } else {
      firstDiv.setAttribute("style", "text-align: left;");
    }
  }

  return div.innerHTML;
}

export default function ToolsField({
  Title = "Result",
  EditorRef = "",
  handlePost,
  height = "51.5vh",
  overallHeight = "67vh",
  EditorHeight = "53vh",
  addSpaceOnTab = false,
  EditorDisable = false,
  errorMsg,
  disableSave = false,
  showMic = true,
  maxChar,
  micTextEditDisabled = false,
}) {
  const editorRef = useRef(null);
  const clickKeyRef = useRef(false);
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [justifyLeft, setJustifyLeft] = useState(true);
  const [justifyCenter, setJustifyCenter] = useState(false);
  const [justifyRight, setJustifyRight] = useState(false);
  const [bulletList, setBulletList] = useState(false);
  const [orderedList, setOrderedList] = useState(false);
  const [tableMenuAnchor, setTableMenuAnchor] = useState(null);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [listenPopUp, setListenPopUp] = useState(false);

  useEffect(() => {
    if (!editorRef.current) return;
    const next = EditorRef ?? "";
    if (editorRef.current.innerHTML !== next) {
      editorRef.current.innerHTML = next;
    }
  }, [EditorRef]);

  useEffect(() => {
    if (!listenPopUp && editorRef.current) {
      editorRef.current.focus();
    }
  }, [listenPopUp]);

  const listenPopUpClose = useCallback(() => {
    clickKeyRef.current = false;
    setListenPopUp(false);
  }, []);

  const voiceToTextgetter = useCallback(
    (textStorage) => {
      if (!textStorage || !clickKeyRef.current) return;
      setTimeout(() => {
        const editor = editorRef.current;
        if (!editor) return;
        editor.focus();
        const textToInsert = ` ${(textStorage || "").trim()}`;
        if (!maxChar) {
          document.execCommand("insertText", false, textToInsert);
          return;
        }
        const currentLength = (editor.innerText || "").replace(/\n+/g, "\n").trim().length;
        const remaining = maxChar - currentLength;
        if (remaining <= 0) {
          errorMsg?.(`You have reached the ${maxChar}-character limit`);
          return;
        }
        const allowedText = textToInsert.slice(0, remaining + 1);
        document.execCommand("insertText", false, allowedText);
        if (textToInsert.length > remaining + 1) {
          errorMsg?.(`You have reached the ${maxChar}-character limit`);
        }
      }, 0);
    },
    [maxChar, errorMsg]
  );

  const handleMicClick = useCallback(() => {
    clickKeyRef.current = true;
    setListenPopUp(true);
  }, []);

  const handleStyleChange = (command) => {
    document.execCommand(command, false, null);
    editorRef.current?.focus();
    switch (command) {
      case "bold":
        setBold((b) => !b);
        break;
      case "italic":
        setItalic((i) => !i);
        break;
      case "underline":
        setUnderline((u) => !u);
        break;
      case "justifyLeft":
        setJustifyLeft(true);
        setJustifyCenter(false);
        setJustifyRight(false);
        break;
      case "justifyCenter":
        setJustifyLeft(false);
        setJustifyCenter(true);
        setJustifyRight(false);
        break;
      case "justifyRight":
        setJustifyLeft(false);
        setJustifyCenter(false);
        setJustifyRight(true);
        break;
      case "insertUnorderedList":
        setBulletList((b) => !b);
        setOrderedList(false);
        break;
      case "insertOrderedList":
        setOrderedList((o) => !o);
        setBulletList(false);
        break;
      default:
        break;
    }
  };

  const generateTableHTML = (rows, cols) => {
    let html = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0; border: 1px solid #ccc;">';
    for (let i = 0; i < rows; i++) {
      html += "<tr>";
      for (let j = 0; j < cols; j++) {
        html += '<td style="border: 1px solid #ccc; padding: 8px; min-width: 60px; vertical-align: top;">&nbsp;</td>';
      }
      html += "</tr>";
    }
    html += "</table>";
    return html;
  };

  const handleTableInsert = () => {
    document.execCommand("insertHTML", false, generateTableHTML(tableRows, tableCols));
    editorRef.current?.focus();
    setTableMenuAnchor(null);
  };

  const getTextStylesAtCursor = () => {
    setBold(false);
    setItalic(false);
    setUnderline(false);
    setJustifyLeft(false);
    setJustifyCenter(false);
    setJustifyRight(false);
    setBulletList(false);
    setOrderedList(false);
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    const node = range.commonAncestorContainer;
    let current = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    while (current && current !== editorRef.current) {
      if (current.tagName === "UL") {
        setBulletList(true);
        break;
      }
      if (current.tagName === "OL") {
        setOrderedList(true);
        break;
      }
      current = current.parentElement;
    }
    const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    const style = el && window.getComputedStyle(el);
    if (!style) return;
    if (style.fontStyle === "italic" || document.queryCommandState("italic")) setItalic(true);
    if (style.fontWeight === "700" || document.queryCommandState("bold")) setBold(true);
    if (style.textDecoration?.includes("underline") || document.queryCommandState("underline")) setUnderline(true);
    if (style.textAlign === "left") setJustifyLeft(true);
    if (style.textAlign === "center") setJustifyCenter(true);
    if (style.textAlign === "right") setJustifyRight(true);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const plainText = e.clipboardData.getData("text/plain");
    const normalized = plainText.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
    document.execCommand("insertText", false, normalized);
  };

  const handleKeyDown = (e) => {
    if (addSpaceOnTab && e.key === "Tab") {
      e.preventDefault();
      document.execCommand("insertHTML", false, "\u00A0\u00A0\u00A0\u00A0\u00A0");
    }
  };

  const handleClear = () => {
    if (editorRef.current) editorRef.current.innerHTML = "";
    setBold(false);
    setItalic(false);
    setUnderline(false);
    setJustifyLeft(true);
    setJustifyCenter(false);
    setJustifyRight(false);
    setBulletList(false);
    setOrderedList(false);
  };

  const handleComplete = () => {
    if (!editorRef.current) return;
    const cleaned = removeEmptyStyleTagsAndReplaceSpan(editorRef.current.innerHTML);
    handlePost?.(cleaned);
  };

  return (
    <Paper component="div" className="rad_tst_tmp_content" sx={{ height: overallHeight }} elevation={1}>
      <Box
        component="div"
        borderBottom="1px solid lightgray"
        pl="0.5vw"
        height="3.5vw"
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="flex-start"
      >
        <Box display="flex" alignItems="center" width="100%">
          <Typography fontWeight={600}>{Title}</Typography>
          <Box className="toolbar" display="flex" flexDirection="row" alignItems="center" sx={{ cursor: "pointer", ml: 1 }}>
            <ButtonGroup variant="text" aria-label="formatting" sx={{ visibility: EditorDisable ? "hidden" : "visible" }}>
              <Tooltip title="Bold" placement="top" arrow>
                <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("bold")} size="small">
                  <FormatBoldIcon color={bold ? "primary" : "inherit"} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Italic" placement="top" arrow>
                <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("italic")} size="small">
                  <FormatItalicIcon color={italic ? "primary" : "inherit"} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Underline" placement="top" arrow>
                <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("underline")} size="small">
                  <FormatUnderlinedIcon color={underline ? "primary" : "inherit"} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Left Align" placement="top" arrow>
                <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("justifyLeft")} size="small">
                  <FormatAlignLeftIcon color={justifyLeft ? "primary" : "inherit"} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Center Align" placement="top" arrow>
                <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("justifyCenter")} size="small">
                  <FormatAlignCenterIcon color={justifyCenter ? "primary" : "inherit"} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Right Align" placement="top" arrow>
                <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("justifyRight")} size="small">
                  <FormatAlignRightIcon color={justifyRight ? "primary" : "inherit"} />
                </IconButton>
              </Tooltip>
              <Box sx={{ borderLeft: 1, display: "flex", borderColor: "grey.500", ml: 1, pl: 1 }}>
                <Tooltip title="Bullet List" placement="top" arrow>
                  <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("insertUnorderedList")} size="small">
                    <FormatListBulletedIcon color={bulletList ? "primary" : "inherit"} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Numbered List" placement="top" arrow>
                  <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("insertOrderedList")} size="small">
                    <FormatListNumberedIcon color={orderedList ? "primary" : "inherit"} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ borderLeft: 1, display: "flex", borderColor: "grey.500", ml: 1, pl: 1 }}>
                <Tooltip title="Insert Table" placement="top" arrow>
                  <IconButton disabled={EditorDisable} onClick={(e) => setTableMenuAnchor(e.currentTarget)} size="small">
                    <TableChartIcon color="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            </ButtonGroup>
          </Box>
        </Box>
          {showMic && (
            <Tooltip title="Mic" placement="top" arrow>
              <IconButton
                disabled={EditorDisable}
                onClick={handleMicClick}
                size="small"
                sx={{ visibility: EditorDisable ? "hidden" : "visible" }}
              >
                <MicIcon sx={{ color: "inherit", cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
          )}
      </Box>

      <Menu anchorEl={tableMenuAnchor} open={Boolean(tableMenuAnchor)} onClose={() => setTableMenuAnchor(null)} PaperProps={{ style: { padding: 16, minWidth: 200 } }}>
        <MenuItem disabled><Typography variant="subtitle1" fontWeight="bold">Insert Table</Typography></MenuItem>
        <MenuItem>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}><Typography variant="body2">Rows:</Typography></Grid>
            <Grid item xs={6}>
              <TextField type="number" size="small" value={tableRows} onChange={(e) => setTableRows(Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1)))} inputProps={{ min: 1, max: 10 }} />
            </Grid>
          </Grid>
        </MenuItem>
        <MenuItem>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}><Typography variant="body2">Columns:</Typography></Grid>
            <Grid item xs={6}>
              <TextField type="number" size="small" value={tableCols} onChange={(e) => setTableCols(Math.max(1, Math.min(8, parseInt(e.target.value, 10) || 1)))} inputProps={{ min: 1, max: 8 }} />
            </Grid>
          </Grid>
        </MenuItem>
        <MenuItem onClick={handleTableInsert}>
          <Button variant="contained" fullWidth>Insert Table ({tableRows}x{tableCols})</Button>
        </MenuItem>
      </Menu>

      <Box component="div" borderBottom="1px solid lightgray" height={height}>
        <Box
          ref={editorRef}
          component="div"
          contentEditable={!EditorDisable}
          className="editor"
          role="textbox"
          aria-multiline="true"
          suppressContentEditableWarning
          onMouseUp={getTextStylesAtCursor}
          onSelect={getTextStylesAtCursor}
          onPaste={handlePaste}
          onKeyDown={addSpaceOnTab ? handleKeyDown : undefined}
          sx={{
            height: EditorHeight,
            overflow: "auto",
            outline: "none",
            p: 1,
            fontSize: "0.9dvw",
            "&:empty::before": { content: "'Enter text...'", color: "#999" },
            "& ul, & ol": { margin: 0, paddingLeft: "24px" },
            "& li": { marginBottom: "2px" },
            "& table": { borderCollapse: "collapse", width: "100%", margin: "10px 0", border: "1px solid #ccc" },
            "& td": { border: "1px solid #ccc", padding: "8px", minWidth: "60px", verticalAlign: "top" },
            "& th": { border: "1px solid #ccc", padding: "8px", backgroundColor: "#f5f5f5", fontWeight: "bold", textAlign: "left" },
          }}
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" height="8vh" sx={{ margin: "0.3vh" }}>
        <Button
          variant="outlined"
          sx={{ textTransform: "capitalize", height: "2vw", mr: "1vw" }}
          onClick={handleClear}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          sx={{ textTransform: "capitalize", height: "2vw", mr: "1vw" }}
          disabled={disableSave || EditorDisable}
          onClick={handleComplete}
        >
          Save
        </Button>
      </Box>
      {listenPopUp && (
        <VoiceToText
          voiceTextConverter={voiceToTextgetter}
          listenPopUp={listenPopUpClose}
          micTextEditDisabled={micTextEditDisabled}
        />
      )}
    </Paper>
  );
}
